import React, {useState} from 'react';

import {BackpackIcon, FlashIcon, NoteIcon, PuzzleIcon, ScriptIcon, SpellBookIcon, StatsIcon, Tab, Tabs} from '@kit';

import {useCharacter} from '../character-context';
import {spellPreparations} from '../model/spell-list';
import {ABILITIES} from '../model/abilities';
import {AbilityCard} from './abilities-block';
import {BottomNav} from './bottom-nav';
import {ActionsTab} from './actions-tab';
import {CardGrid} from './card-grid';
import {ConditionsBar} from './conditions-bar';
import {SensesBlock} from './defenses-block';
import {EquipmentTab} from './equipment-tab';
import {FeaturesTab} from './features-tab';
import {NotesTab} from './notes-tab';
import {OriginTab} from './origin-tab';
import {ProficienciesBlock} from './proficiencies-block';
import {SheetHeader} from './sheet-header';
import {freePreparationCount, SpellsTab} from './spells-tab';
import {SummaryStrip} from './summary-strip';
import {useWideScreen} from './use-wide-screen';
import {VitalsBlock} from './vitals-block';

import css from './sheet.module.css';

type ScreenIcon = React.FC<{appearance?: 'regular' | 'filled'; size?: 24}>;

type SheetSection = {
	key: string;
	title: string;
	/** подпись вкладки на широком экране, если отличается */
	tabTitle?: string;
	Icon: ScreenIcon;
	Content: React.FC;
};

/** плашки характеристик, умений и восприятия в общей сетке */
const AbilitiesScreen: React.FC = () => (
	<CardGrid>
		{ABILITIES.map((ability) => (
			<AbilityCard key={ability} ability={ability} />
		))}
		<ProficienciesBlock />
		<SensesBlock />
	</CardGrid>
);

/** на широком экране это вкладки; на узком — экраны нижней навигации вместе с характеристиками */
const TAB_SECTIONS: SheetSection[] = [
	{key: 'actions', title: 'Действия', Icon: FlashIcon, Content: ActionsTab},
	{key: 'spells', title: 'Заклинания', Icon: SpellBookIcon, Content: SpellsTab},
	{key: 'equipment', title: 'Снаряжение', Icon: BackpackIcon, Content: EquipmentTab},
	{key: 'features', title: 'Особенности', tabTitle: 'Особенности и черты', Icon: PuzzleIcon, Content: FeaturesTab},
	{key: 'origin', title: 'Происхождение', Icon: ScriptIcon, Content: OriginTab},
	{key: 'notes', title: 'Заметки', Icon: NoteIcon, Content: NotesTab},
];

const SCREENS: SheetSection[] = [
	{key: 'abilities', title: 'Характеристики', Icon: StatsIcon, Content: AbilitiesScreen},
	...TAB_SECTIONS,
];

type Props = {
	/** открыть конструктор с текущими выборами */
	onOpenConstructor(): void;
};

/** на широком экране всё видно сразу, на узком — один экран, экраны переключаются кнопками снизу */
export const CharacterSheet: React.FC<Props> = ({onOpenConstructor}) => {
	const isWide = useWideScreen();

	return (
		<article className={css.sheet}>
			<SheetHeader onOpenConstructor={onOpenConstructor} />
			{isWide ? <WideLayout /> : <NarrowLayout />}
		</article>
	);
};

const WideLayout: React.FC = () => (
	<div className={css.columns}>
		<div className={css.screen}>
			<AbilitiesScreen />
		</div>
		<div className={css.rightColumn}>
			<ConditionsBar />
			<VitalsBlock />
			<SheetTabs />
		</div>
	</div>
);

const NarrowLayout: React.FC = () => {
	const [screen, setScreen] = useState('actions');
	const hasFreePreparations = useHasFreePreparations();
	const {Content} = SCREENS.find(({key}) => key === screen) || SCREENS[0];

	const openScreen = (next: string) => {
		setScreen(next);
		window.scrollTo({top: 0});
	};

	return (
		<div className={css.narrowLayout}>
			<SummaryStrip />
			<div className={css.screen}>
				<Content />
			</div>
			<BottomNav
				screens={SCREENS.map(({key, title, Icon}) => ({
					key,
					title,
					Icon,
					hasBadge: key === 'spells' && hasFreePreparations,
				}))}
				current={screen}
				onOpen={openScreen}
			/>
		</div>
	);
};

function useHasFreePreparations() {
	const {catalog, character} = useCharacter();
	return character.canCastSpells && freePreparationCount(spellPreparations(catalog, character)) > 0;
}

const SheetTabs: React.FC = () => {
	const hasFreePreparations = useHasFreePreparations();

	return (
		<Tabs className={css.tabs}>
			{TAB_SECTIONS.map(({key, title, tabTitle, Content}) => (
				<Tab
					key={key}
					header={
						<>
							{tabTitle || title}
							{key === 'spells' && hasFreePreparations && (
								<span className={css.tabBadge} title="Можно подготовить заклинания" />
							)}
						</>
					}
				>
					<Content />
				</Tab>
			))}
		</Tabs>
	);
};
